import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  AccountIdSchema,
  ConnectionActivitySchema,
  ConnectionUpdateSchema,
  GenericConnectionId,
  mcpExposeMetadata,
  OKGeneric,
  PresentableConnectionSchema,
  ProviderSetupFieldSchema,
  ResponseMetaSchema,
} from '../models';
import { WebhookReadSchema } from '../models/integrations/integration.models.webhook';

extendZodWithOpenApi(z);

const c = initContract();

export const apiConnections = c.router({
  getConnectionId: {
    method: 'GET',
    path: `/1.0/connections/:connectionId`,
    pathParams: z.object({ connectionId: GenericConnectionId }),
    responses: {
      200: PresentableConnectionSchema,
    },
    summary: 'Get connection details by ID',
    description:
      'Retrieves the full details of a specific integration connection including its current status, settings, provider information, and associated metadata.',
    metadata: mcpExposeMetadata,
  },
  updateConnectionId: {
    method: 'POST',
    path: `/1.0/connections/:connectionId/update`,
    pathParams: z.object({ connectionId: GenericConnectionId }),
    body: ConnectionUpdateSchema,
    responses: {
      200: PresentableConnectionSchema,
    },
    summary: 'Update the connection, usually to adjust `connection.settings`',
    description:
      "Updates a connection's configuration, typically used to modify connection-specific settings like sync preferences, filters, or display options. Returns the updated connection details.",
  },
  resetConnectionId: {
    method: 'POST',
    path: `/1.0/connections/:connectionId/reset`,
    pathParams: z.object({ connectionId: GenericConnectionId }),
    body: c.noBody(),
    responses: {
      200: OKGeneric,
    },
    summary:
      'TODO: Resets the connection to initial state, removing webhooks and all imported data, to start fresh.',
    description:
      'Resets a connection to its initial state by removing all webhooks and imported data, allowing users to start fresh. The OAuth credentials are preserved, so re-authentication is not required.',
  },
  enableConnectionId: {
    method: 'POST',
    path: `/1.0/connections/:connectionId/enable`,
    pathParams: z.object({ connectionId: GenericConnectionId }),
    body: c.noBody(),
    responses: {
      200: OKGeneric,
    },
    summary: 'Re-enable a previously disabled connection',
    description:
      'Re-enables a previously disabled connection, restoring its active state. This re-activates synchronization and webhook processing for the connection.',
  },
  disconnectConnectionId: {
    method: 'DELETE',
    path: `/1.0/connections/:connectionId/disconnect`,
    pathParams: z.object({ connectionId: GenericConnectionId }),
    body: c.noBody(),
    responses: {
      200: OKGeneric,
    },
    summary:
      'Disconnects the connection, removes webhooks, but keeps associated data/resources/identities.',
    description:
      'Disconnects an integration connection by removing webhooks and disabling synchronization. All previously imported data, resources, and identity associations are preserved. The OAuth connection is revoked, requiring re-authentication to reconnect.',
  },
  deleteConnectionId: {
    method: 'DELETE',
    path: `/1.0/connections/:connectionId/delete`,
    pathParams: z.object({ connectionId: GenericConnectionId }),
    body: c.noBody(),
    responses: {
      200: OKGeneric,
    },
    summary: 'TODO: Deletes the connection, including all data and webhooks associated.',
    description:
      'Permanently deletes a connection and all associated data including imported resources, webhooks, identities, and synchronization history. This action is irreversible.',
  },
  getConnectionActivities: {
    method: 'GET',
    path: `/1.0/connection/activities`,
    query: z.object({
      connectionId: z.string().uuid().openapi({
        description: 'Required: Filter activities by connection',
        example: 'c70c313b-421e-432c-a28a-20ad04dff9d6',
      }),
      toolId: z.string().optional().openapi({ description: 'Filter by tool', example: 'github' }),
      accountId: AccountIdSchema.optional().openapi({
        description: 'Account ID',
        example: 'c70c313b-421e-432c-a28a-20ad04dff9d6',
      }),
      page: z.coerce
        .number()
        .int()
        .optional()
        .default(1)
        .openapi({ description: 'Page number', example: 1 }),
      limit: z.coerce
        .number()
        .int()
        .optional()
        .default(100)
        .openapi({ description: 'Results per page', example: 100 }),
    }),
    responses: {
      200: z.object({
        activities: z.array(ConnectionActivitySchema),
        meta: ResponseMetaSchema.pick({ count: true, totalCount: true }),
      }),
    },
    summary: 'Gets the activities for a given connection',
    description:
      'Retrieves a paginated list of activity logs for connections, filterable by tool, account, or specific connection. Activities include sync events, webhook deliveries, errors, and other operational events.',
    metadata: mcpExposeMetadata,
  },
  getConnectionWebhooks: {
    method: 'GET',
    path: `/1.0/connections/:connectionId/webhooks`,
    pathParams: z.object({ connectionId: GenericConnectionId }),
    responses: {
      200: z.array(WebhookReadSchema),
    },
    summary: 'Gets webhook status and details for a given connection',
    description:
      'Retrieves all webhooks registered for a specific connection, including their current status, endpoint URLs, subscribed events, and last delivery information. Useful for debugging webhook issues or monitoring webhook health.',
    metadata: mcpExposeMetadata,
  },
  takeoverWebhooks: {
    method: 'POST',
    path: `/1.0/connections/:connectionId/webhooks/takeover`,
    pathParams: z.object({ connectionId: GenericConnectionId }),
    body: c.noBody(),
    responses: {
      200: z.object({
        message: z.string(),
        deletedWebhooks: z
          .array(
            z.object({
              id: z.union([z.string(), z.number()]), // Jira returns numeric IDs
              url: z.string(),
            })
          )
          .optional(),
        syncResult: z.object({}).passthrough().nullish(),
      }),
    },
    summary:
      'Takeover existing webhooks for Jira connections by removing all existing webhooks and re-syncing',
    description:
      'Performs a webhook takeover operation, primarily for Jira connections. Removes all existing webhooks registered with the provider and re-creates them with the current TIC endpoints. Useful when webhooks are pointing to stale or incorrect URLs, or when troubleshooting webhook delivery issues.',
  },
  getConnectionProviderCustomSettingsFields: {
    method: 'GET',
    path: '/1.0/connections/:connectionId/provider/custom-settings-fields',
    pathParams: z.object({ connectionId: GenericConnectionId }),
    responses: {
      200: z.object({
        form_fields: z.array(ProviderSetupFieldSchema),
      }),
      400: z.object({ message: z.string() }),
      404: z.object({ message: z.string() }),
    },
    summary: 'Fetch provider-defined custom settings fields',
    description:
      'Returns provider-normalized setup fields that write to connection.settings for the active import profile.',
  },
  reinstallConnectionWebhooks: {
    method: 'POST',
    path: `/1.0/connections/:connectionId/webhooks/reinstall`,
    pathParams: z.object({ connectionId: GenericConnectionId }),
    body: c.noBody(),
    responses: {
      200: z.object({
        message: z.string(),
        job_id: z
          .string()
          .describe('Log correlation id for the background reinstall task (fire-and-forget)'),
      }),
    },
    summary: 'Reinstall known webhooks for a connection (internal-only)',
    description:
      'Runs a safe uninstall-then-install cycle for webhooks associated with a single connection using TIC local webhook definitions. Intended for internal debugging and recovery when remote webhook state drifts from local state.',
  },
});
